import axios from 'axios';
import { GetPostsStatusResult, PostState } from 'common/interfaces/api';
import { cn, useOutsideClick } from 'common/utils';
import ReportBox from 'components/common/ReportBox';
import {
  PenIcon,
  PinIcon,
  ThreeDotsIcon,
  TrashIcon,
} from 'components/v2/atoms/icon';
import ConfirmDialog from 'components/v2/molecules/confirm-dialog';
import InfoDialog from 'components/v2/molecules/info-dialog';
import { useRouter } from 'next/router';
import React, { useContext, useEffect, useRef, useState } from 'react';
import useGlobalStore from 'store/global';
import useStore from 'store/timeline';

import { PostItemContext } from './post-item-context';

interface ThreeDotsProps {
  isOwnPost?: boolean;
  isAdsPost?: boolean;
  isStreamingPost?: boolean;
  isProcessing?: boolean;
  isPinned?: boolean;
  isProfilePage?: boolean;
  isGoldPost?: boolean;
  isPostDetail?: boolean;
  isSavedPost?: boolean;
  onDelete?: () => void;
  onEdit?: () => void;
  onBlock?: () => void;
  onHide?: () => void;
  setIsOpenThreeDots: (isOpen: boolean) => void;
  isOpenThreeDots: boolean;
}

const ThreeDots = ({
  isOwnPost = false,
  isAdsPost = false,
  isStreamingPost = false,
  isProcessing = false,
  isPinned = false,
  isProfilePage = false,
  isGoldPost = false,
  isPostDetail = false,
  isSavedPost,
  onDelete,
  onEdit,
  onBlock,
  onHide,
  setIsOpenThreeDots,
  isOpenThreeDots,
}: ThreeDotsProps) => {
  const { post } = useContext(PostItemContext);
  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage,
  );
  const refetchPosts = useStore((state) => state.refetchPosts);
  const router = useRouter();
  const [saved, setSaved] = useState(
    isSavedPost !== undefined ? isSavedPost : !!post?.is_saved,
  );

  // Sync saved state when isSavedPost prop changes
  useEffect(() => {
    if (isSavedPost !== undefined) {
      setSaved(isSavedPost);
    }
  }, [isSavedPost]);
  const [openConfirmDeletePost, setOpenConfirmDeletePost] = useState(false);
  const [openConfirmReportPost, setOpenConfirmReportPost] = useState(false);
  const [openConfirmBlockUser, setOpenConfirmBlockUser] = useState(false);
  const [openConfirmReportUser, setOpenConfirmReportUser] = useState(false);
  const [openConfirmHidePost, setOpenConfirmHidePost] = useState(false);
  const postDetailUrl = `/${post?.user_info?.username}/posts/${post?.id}`;
  const threeDotRef = useRef<HTMLDivElement>(null);
  const currentUser = useStore((state) => state.currentUser);
  useOutsideClick(threeDotRef, () => {
    if (isOpenThreeDots) setIsOpenThreeDots(false);
  });

  if (!post || !currentUser) return null;

  const handleDeletePostClick = () => {
    setOpenConfirmDeletePost(true);
  };

  const handleBlockUser = async () => {
    try {
      setOpenConfirmBlockUser(false);
      await axios.post(`/users/blocks`, {
        user_id: post.uid,
        action: 'block',
      });
      setSnackbarMessage({
        text: 'ユーザーがブロックされました',
        type: 'success',
      });
      if (onBlock) onBlock();
    } catch (e) {
      setSnackbarMessage({
        type: 'error',
        text: 'ユーザーのブロックが失敗しました',
      });
    }
    setIsOpenThreeDots(false);
  };

  const handleDeletePost = async () => {
    try {
      setOpenConfirmDeletePost(false);
      await axios.delete(`/posts/${post.id}`);
      setSnackbarMessage({ text: '投稿が削除されました', type: 'success' });
      if (onDelete) onDelete();
      setTimeout(() => {
        refetchPosts();
      }, 200);
    } catch (e) {
      setSnackbarMessage({
        type: 'error',
        text: '投稿の削除が失敗しました',
      });
    }
    setIsOpenThreeDots(false);
  };

  const handleHidePostClick = async () => {
    try {
      setOpenConfirmHidePost(false);
      await axios.post(`/posts/${post.id}/hides`, {});
      setSnackbarMessage({
        type: 'success',
        text: 'タイムラインから非表示しました。',
      });
      if (onHide) onHide();
    } catch (e) {
      setSnackbarMessage({
        type: 'error',
        text: '投稿の非表示が失敗しました',
      });
    }
    setIsOpenThreeDots(false);
  };

  const handleSavePostClick = async (action: 'save' | 'unsave') => {
    try {
      await axios.post(`/posts/saves`, {
        post_id: post.id,
        action: action,
      });
      const newSavedState = action === 'save';
      setSaved(newSavedState);
      setSnackbarMessage({
        type: 'success',
        text:
          action === 'save'
            ? '投稿を保存しました'
            : '保存した投稿から外しました',
      });
    } catch (e) {
      setSnackbarMessage({ text: 'エラーが発生しました', type: 'error' });
    }
  };

  const handlePinPostClick = async () => {
    try {
      await axios.post(
        `/profiles/${isPinned ? 'unpin_post' : 'pin_post'}/${post.id}`,
        {},
      );
      setSnackbarMessage({
        type: 'success',
        text: `${
          isPinned
            ? 'プロフィールの固定を外しました'
            : 'プロフィールに固定しました'
        }`,
      });
      setTimeout(() => {
        if (isProfilePage) refetchPosts();
      }, 200);
    } catch (e) {
      setSnackbarMessage({
        type: 'error',
        text: 'エラーが発生しました',
      });
    }
    setIsOpenThreeDots(false);
  };

  const handleEditClick = async () => {
    if (isProcessing) {
      const postStatus = (await axios.get(`/posts/status?ids=${post.id}`))
        .data as GetPostsStatusResult;
      if (
        postStatus?.data &&
        postStatus?.data[0]?.status !== PostState.OnProcessing
      ) {
        onEdit?.();
        void router.push(postDetailUrl);
      } else {
        setSnackbarMessage({
          type: 'warning',
          text: '処理中のため、少しお待ちください',
        });
      }
      setIsOpenThreeDots(false);
      return;
    }
    onEdit?.();
    void router.push(postDetailUrl);
    setIsOpenThreeDots(false);
  };

  // Simple SVG icons
  const BookmarkIcon = ({ className }: { className?: string }) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
    </svg>
  );

  const ReportPostIcon = () => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="size-5"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M3 3h18v18H3V3z"></path>
      <path d="M12 8v4"></path>
      <path d="M12 16h.01"></path>
    </svg>
  );

  const ReportUserIcon = () => (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="size-5 text-pp-error-primary"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M12 5.99L19.53 19H4.47L12 5.99M12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v4h2v-4z"></path>
    </svg>
  );

  const EyeSlashIcon = () => (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="size-5"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"></path>
    </svg>
  );

  const UserBlockIcon = () => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="#EF4444"
      strokeWidth="2"
      className="size-5 text-pp-error-primary"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  );

  const MenuItem = ({
    icon,
    text,
    onClick,
    danger = false,
    hidden = false,
  }: {
    icon: React.ReactNode;
    text: string;
    onClick: () => void;
    danger?: boolean;
    hidden?: boolean;
  }) => {
    if (hidden) return null;

    return (
      <li>
        <button
          onClick={onClick}
          className={cn(
            'flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-normal transition-colors hover:bg-gray-50',
            danger ? 'text-pp-error-primary' : 'text-pp-text-primary',
          )}
        >
          <div className="flex size-5 items-center justify-center">{icon}</div>
          <span>{text}</span>
        </button>
      </li>
    );
  };

  const ownPostContent = (
    <ul className="rounded-lg">
      <MenuItem
        icon={
          <BookmarkIcon
            className={cn('size-5 text-pp-text-primary', {
              'fill-pp-green-primary text-pp-green-primary': saved,
            })}
          />
        }
        text={saved ? '保存した投稿から外す' : '投稿を保存する'}
        onClick={() => handleSavePostClick(saved ? 'unsave' : 'save')}
      />
      <MenuItem
        icon={<PenIcon className="size-5" />}
        text="詳細画面へ移動する"
        onClick={handleEditClick}
        hidden={isStreamingPost || isAdsPost || isGoldPost}
      />
      <MenuItem
        icon={<PinIcon className="size-5" />}
        text={isPinned ? 'プロフィールの固定を外す' : 'プロフィールに固定する'}
        onClick={handlePinPostClick}
        hidden={!isProfilePage && !isPostDetail}
      />
      <MenuItem
        icon={<TrashIcon className="size-5 text-pp-error-primary" />}
        text="投稿を削除する"
        onClick={handleDeletePostClick}
        danger
        hidden={!isProfilePage && !isPostDetail}
      />
    </ul>
  );

  const otherPostContent = (
    <ul className="rounded-lg">
      <MenuItem
        icon={
          <BookmarkIcon
            className={cn('size-5 stroke-pp-text-primary', {
              'fill-pp-green-primary stroke-pp-green-primary': saved,
            })}
          />
        }
        text={saved ? '保存した投稿から外す' : '投稿を保存する'}
        onClick={() => handleSavePostClick(saved ? 'unsave' : 'save')}
      />
      <MenuItem
        icon={<ReportPostIcon />}
        text="投稿問題をレポートする"
        onClick={() => setOpenConfirmReportPost(true)}
      />
      <MenuItem
        icon={<EyeSlashIcon />}
        text="投稿を非表示にする"
        onClick={() => setOpenConfirmHidePost(true)}
        hidden={isAdsPost}
      />
      <MenuItem
        icon={<UserBlockIcon />}
        text="ユーザーをブロックする"
        danger
        onClick={() => setOpenConfirmBlockUser(true)}
        hidden={isAdsPost}
      />
      <MenuItem
        icon={<ReportUserIcon />}
        text="ユーザーをレポートする"
        danger
        onClick={() => setOpenConfirmReportUser(true)}
      />
    </ul>
  );

  return (
    <div className="relative" ref={threeDotRef}>
      <button
        onClick={() => setIsOpenThreeDots(!isOpenThreeDots)}
        className="flex size-9 items-center justify-center rounded-full transition-colors hover:bg-gray-100"
      >
        <ThreeDotsIcon className="size-6 rotate-90 fill-pp-text-primary" />
      </button>

      {isOpenThreeDots && (
        <div className="absolute right-0 top-full z-50 w-60 overflow-hidden rounded-lg border border-pp-border-primary bg-white shadow-lg">
          <div className="relative">
            {isOwnPost ? ownPostContent : otherPostContent}
          </div>
        </div>
      )}
      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={openConfirmDeletePost}
        title="本当に削除しますか？"
        message=""
        confirmText="削除する"
        cancelText="キャンセル"
        confirmVariant="redDefault"
        onConfirm={handleDeletePost}
        onCancel={() => setOpenConfirmDeletePost(false)}
      />

      {/* Confirm Block User Dialog */}
      <ConfirmDialog
        open={openConfirmBlockUser}
        title="本当にブロックしますか？"
        message=""
        confirmText="ブロックする"
        cancelText="キャンセル"
        confirmVariant="redDefault"
        onConfirm={handleBlockUser}
        onCancel={() => setOpenConfirmBlockUser(false)}
      />

      {/* Confirm Hide Post Dialog */}

      <ConfirmDialog
        open={openConfirmHidePost}
        title="本当に非表示にしますか？"
        message=""
        confirmText="非表示にする"
        cancelText="キャンセル"
        confirmVariant="redDefault"
        onConfirm={handleHidePostClick}
        onCancel={() => setOpenConfirmHidePost(false)}
      />

      {/* Report Post Dialog */}
      {openConfirmReportPost && (
        <InfoDialog
          open={openConfirmReportPost}
          title="オプション選択"
          content={
            <div className="relative w-full max-w-md rounded-lg bg-white">
              <ul className="space-y-2">
                <li>
                  <a
                    href="https://help.postprime.com/hc/ja/requests/new?ticket_form_id=360008586356"
                    target="_blank"
                    rel="noopener"
                    className="block rounded-md px-3 py-2 text-sm text-blue-600 hover:bg-blue-50"
                  >
                    著作権侵害の報告
                  </a>
                </li>
                <li>
                  <a
                    href="https://help.postprime.com/hc/ja/requests/new?ticket_form_id=360007972215"
                    target="_blank"
                    rel="noopener"
                    className="block rounded-md px-3 py-2 text-sm text-blue-600 hover:bg-blue-50"
                  >
                    個人情報に関する苦情
                  </a>
                </li>
                <li>
                  <a
                    href="https://help.postprime.com/hc/ja/requests/new?ticket_form_id=360008586376"
                    target="_blank"
                    rel="noopener"
                    className="block rounded-md px-3 py-2 text-sm text-blue-600 hover:bg-blue-50"
                  >
                    その他の違反報告
                  </a>
                </li>
              </ul>
            </div>
          }
          onClose={() => setOpenConfirmReportPost(false)}
        />
      )}

      {/* Report User Dialog */}
      {openConfirmReportUser && (
        <InfoDialog
          open={openConfirmReportUser}
          title="ユーザーをレポートする"
          content={
            <ReportBox
              targetUserId={post.uid}
              onDialogSubmit={() => setOpenConfirmReportUser(false)}
            />
          }
          onClose={() => setOpenConfirmReportUser(false)}
        />
      )}
    </div>
  );
};

export default ThreeDots;
